import React, { useEffect, useRef } from 'react';

let YT_API_LOADING = false;
let YT_API_LOADED = false;
const callbacks = [];

function loadYouTubeAPI(cb) {
  if (YT_API_LOADED) { cb(); return; }
  callbacks.push(cb);
  if (YT_API_LOADING) return;
  YT_API_LOADING = true;
  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  window.onYouTubeIframeAPIReady = () => {
    YT_API_LOADED = true;
    callbacks.splice(0).forEach(fn => fn());
  };
  document.body.appendChild(tag);
}

const YouTubeAudio = ({ videoId, playing, volume = 20, muted = true, onEnded, onReady, onError, onPlayer }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const lastVideo = useRef(null);

  useEffect(() => {
    if (!playing || !videoId) return;
    let mounted = true;
    loadYouTubeAPI(() => {
      if (!mounted) return;
      if (playerRef.current) return;
      const el = document.createElement('div');
      el.id = `ytp_${Math.random().toString(36).slice(2)}`;
      if (containerRef.current) containerRef.current.appendChild(el);
      // eslint-disable-next-line no-undef
      playerRef.current = new YT.Player(el.id, {
        height: '1',
        width: '1',
        videoId: videoId || '',
        playerVars: {
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          origin: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.setVolume(Math.max(0, Math.min(100, volume)));
              if (muted) e.target.mute();
              else e.target.unMute();
              if (playing && videoId) e.target.playVideo();
            } catch {}
            if (onPlayer) {
              const api = {
                getCurrentTime: () => {
                  try { return e.target.getCurrentTime(); } catch { return 0; }
                },
                getDuration: () => {
                  try { return e.target.getDuration(); } catch { return 0; }
                },
                seekTo: (s) => {
                  try { e.target.seekTo(Math.max(0, s), true); } catch {}
                },
                seekBy: (ds) => {
                  try { const t = e.target.getCurrentTime(); e.target.seekTo(Math.max(0, t + ds), true); } catch {}
                },
                pause: () => { try { e.target.pauseVideo(); } catch {} },
                play: () => { try { e.target.playVideo(); } catch {} },
              };
              onPlayer(api);
            }
            if (onReady) onReady();
          },
          onStateChange: (e) => {
            if (e.data === 0 && onEnded) onEnded();
          },
          onError: () => { if (onError) onError(); },
        },
      });
      lastVideo.current = videoId;
    });
    return () => { mounted = false; };
  }, [playing, videoId]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    if (videoId && videoId !== lastVideo.current) {
      try { p.loadVideoById(videoId); } catch {}
      lastVideo.current = videoId;
    }
  }, [videoId]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    try {
      p.setVolume(Math.max(0, Math.min(100, volume)));
    } catch {}
  }, [volume]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (muted) p.mute(); else p.unMute();
    } catch {}
  }, [muted]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (playing) p.playVideo(); else p.pauseVideo();
    } catch {}
  }, [playing]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        overflow: 'hidden',
        opacity: 0,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
};

export default YouTubeAudio;
